import styled from '@emotion/styled/macro';
import vars from '@variables';
import React, { useEffect, useRef } from 'react';
import { useSearchBox } from 'react-instantsearch';
import { useCustomSearch } from './CustomSearchContext';
import Translate from '../../utils/translation-functions';

const SearchBox = props => {
  const { updateSearchValue } = useCustomSearch();
  const { query, refine } = useSearchBox(props);

  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      emitGAEvent(query);
    }, 750); // 750ms wait after the last keypress to capture users searchTerm when they have finished typing
    return () => clearTimeout(timer);
  }, [query]);

  const title = props.title;
  const className = props.className;

  const emitGAEvent = searchTerm => {
    if (window !== undefined && window.dataLayer !== undefined) {
      window.dataLayer.push({
        event: 'view_search_results',
        eventModel: { search_term: searchTerm },
      });
    }
  };

  function setQuery(newQuery) {
    updateSearchValue(newQuery);
    refine(newQuery);
  }

  return (
    <div className={className}>
      <div className="title-wrap">
        <h1>{title}</h1>
      </div>
      <form
        noValidate=""
        className="ais-SearchBox-form"
        action=""
        role="search"
        onSubmit={e => {
          e.preventDefault();
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-label="Search icon"
        >
          <circle cx="11" cy="11" r="8"></circle>
          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        </svg>
        <input
          ref={inputRef}
          className="ais-SearchBox-input"
          type="search"
          data-testid="searchbox-input"
          placeholder={Translate('Start your search...', true)}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          required=""
          maxLength="512"
          aria-label="Search"
          onChange={event => setQuery(event.target.value)}
          value={query}
        />
        <button
          type="reset"
          title={Translate('Clear the search query', true)}
          className="ais-SearchBox-reset"
          onClick={() => {
            updateSearchValue('');
            refine('');
          }}
        >
          <svg
            className="ais-SearchBox-resetIcon"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            width="10"
            height="10"
            aria-label="Clear search box icon"
          >
            <path d="M8.114 10L.944 2.83 0 1.885 1.886 0l.943.943L10 8.113l7.17-7.17.944-.943L20 1.886l-.943.943-7.17 7.17 7.17 7.17.943.944L18.114 20l-.943-.943-7.17-7.17-7.17 7.17-.944.943L0 18.114l.943-.943L8.113 10z"></path>
          </svg>
        </button>
      </form>
    </div>
  );
};

const StyledSearchBox = styled(SearchBox)`
  padding: 8.5% 10%;
  margin-top: ${vars.primaryNavHeight};
  background-color: #c4c4c4;
  background-image: url(${({ backgroundImageSrc }) =>
    backgroundImageSrc || ''});
  background-size: cover;
  .title-wrap {
    text-align: center;
  }

  h1 {
    font-weight: ${vars.fontWeightExtraBold};
    font-size: 3.8rem;
    color: #2f2f46;
    line-height: 110%;
    padding: 8px;
    background-color: ${vars.white};
    text-align: center;
    display: inline-block;
    margin: 0 auto 22px auto;
  }
  form {
    display: flex;
    align-items: center;
    border: 1px solid #212121;
    width: 100%;
    display: flex;
    padding: 0;
    margin: 0 auto;
    background-color: ${vars.white};
    max-width: ${vars.contentContained};
  }
  form svg {
    margin-left: 2.6rem;
    color: ${vars.coreDarkGrey};
  }
  .ais-SearchBox-form {
    .ais-SearchBox-input {
      padding: ${vars.spacingS} ${vars.spacingXl} ${vars.spacingS} 2.6rem;
      line-height: 4.8rem;
      font-weight: ${vars.fontWeightRegular};
      font-size: 2rem;
      color: ${vars.coreDarkGrey};
      border: none;
      width: 90%;
      outline: none;
      margin: 0;
    }
    .ais-SearchBox-input::placeholder {
      /* Chrome, Firefox, Opera, Safari 10.1+ */
      color: ${vars.coreDarkGrey};
      opacity: 1; /* Firefox */
    }
    .ais-SearchBox-input:-ms-input-placeholder {
      /* Internet Explorer 10-11 */
      color: ${vars.coreDarkGrey};
    }
    .ais-SearchBox-input::-ms-input-placeholder {
      /* Microsoft Edge */
      color: ${vars.coreDarkGrey};
    }
    input[type='search']::-webkit-search-cancel-button {
      /* Remove default */
      -webkit-appearance: none;

      /* Now your own custom styles */
      /* height: 10px;
        width: 10px;
        background: red; */
      /* Will place small red box on the right of input (positioning carries over) */
    }
    .ais-SearchBox-submit,
    .ais-SearchBox-reset {
      width: 5%;
      line-height: 4.8rem;
      width: 5%;
      border: none;
      background-color: transparent;
      outline: none;
      cursor: pointer;
    }
    .ais-SearchBox-submitIcon,
    .ais-SearchBox-resetIcon {
      width: 2rem;
      height: 2rem;
    }
    .ais-SearchBox-reset {
      line-height: normal;
      opacity: 0.5;
      margin-top: 3px;
      margin-right: 16px;
      @media screen and (min-width: ${vars.breakPointSmallTablet}) {
        margin-right: 8px;
      }
    }
    .ais-SearchBox-reset div {
      font-weight: ${vars.fontWeightRegular};
    }
    .ais-SearchBox-resetIcon {
      margin-left: 0;
      width: 1.5rem;
      height: 1.5rem;
    }
    .ais-SearchBox-resetIcon:after {
    }
  }
`;

export default StyledSearchBox;
